import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, CheckCircle2, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/docs`;

// Public page: guest uploads ID photos before arrival through a secret WhatsApp link.
export default function GuestDocsUploadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const fileRef = useRef(null);

  const load = () => axios.get(`${API}/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(e?.response?.status === 410 ? "Questo link è scaduto." : "Link non valido."));
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true); setLast(null);
    try {
      let res = null;
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        res = (await axios.post(`${API}/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      }
      setLast(res); load();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Caricamento non riuscito, riprova."); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="min-h-screen bg-[#2b1410] text-[#f7ebdf] flex items-center justify-center p-5" data-testid="guest-docs-page">
      <div className="w-full max-w-md rounded-3xl bg-[#3a1b15] border border-white/10 p-6 shadow-2xl">
        <div className="flex items-center gap-3">
          <img src="/logo192.png" alt="" className="h-12 w-12 rounded-full bg-white object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div><div className="text-[11px] uppercase tracking-[0.2em] text-[#C48B31]">Il Pozzo dei Giganti</div><h1 className="font-serif text-2xl font-bold">Documenti per il check-in</h1></div>
        </div>
        {err && <p className="mt-5 rounded-xl bg-red-900/40 border border-red-400/40 px-3 py-2 text-sm" data-testid="guest-docs-error">{err}</p>}
        {!info && !err && <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>}
        {info && (
          <>
            <p className="mt-4 text-sm text-[#e8d5c4]">Buongiorno{info.guest ? ` ${info.guest}` : ""}! Per non farvi aspettare all'arrivo, caricate una foto del documento d'identità di <b>ogni ospite</b> (fronte; per la carta d'identità anche il retro). Bastano pochi secondi.</p>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={upload} data-testid="guest-docs-input" />
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="mt-5 w-full h-16 rounded-2xl bg-[#C48B31] hover:bg-[#d79a3c] text-[#2b1410] text-lg font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-colors" data-testid="guest-docs-capture">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className="h-6 w-6" />} {busy ? "Caricamento…" : info.uploaded ? "Aggiungi un altro documento" : "Scatta la foto del documento"}
            </button>
            {info.uploaded > 0 && (
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-900/40 border border-emerald-400/40 px-3 py-2 text-sm" data-testid="guest-docs-count">
                <CheckCircle2 className="h-5 w-5 text-emerald-300" /> {info.uploaded} {info.uploaded === 1 ? "documento ricevuto" : "documenti ricevuti"}. Grazie!
              </div>
            )}
            {last && !last.readable && <p className="mt-2 flex items-start gap-2 text-xs text-amber-200" data-testid="guest-docs-warn"><AlertTriangle className="h-4 w-4 shrink-0" /> La foto è poco leggibile: se possibile rifatela con più luce e il documento ben dritto.</p>}
            <p className="mt-6 flex items-start gap-2 text-[11px] text-[#bfa38f]"><ShieldCheck className="h-4 w-4 shrink-0 text-[#C48B31]" /> Le foto sono usate solo per la registrazione obbligatoria degli alloggiati (art. 109 TULPS), sono conservate in modo privato e vengono eliminate automaticamente {info.retention_days} giorni dopo il check-out. <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-[#f7ebdf]/80" data-testid="guest-docs-privacy-link">Informativa privacy</a></p>
          </>
        )}
      </div>
    </div>
  );
}
